import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Typography,
  IconButton,
  Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Breakpoint, SxProps } from '@mui/system';
import { ReactComponent as CloseSVG } from 'assets/xCircle.svg';
import { KFButton } from '@kf/ic2fx';

export interface IActionWallProps extends DialogProps {
  onCancel?: () => void;
  onSuccess?: () => void;
  onCloseIcon?: () => void;
  title?: string;
  exitOnClickOutside?: boolean;
  cancelLabel?: string;
  successLabel?: string;
  closeIconButton?: boolean;
  sxTitle?: SxProps;
  titleClassName?: string;
  dividers?: boolean;
  successDisabled?: boolean;
  cancelButtonId?: string;
  saveButtonId?: string;
  dialogId?: string;
  size?: string;
  outlinedCancelButton?: boolean;
  footerContent?: React.ReactNode;
  headerContent?: React.ReactNode;
  centerAlignment?: boolean;
  showCancelBtnAsPrimary?: boolean;
  saveBtnSxProps?: SxProps;
  dialogContentSxProps?: SxProps;
  dialogActionsSxProps?: SxProps;
  dialogPaperSxProps?: SxProps;
  dialogTitleSxProps?: any; // TBD
  closeIconStyleProps?: React.CSSProperties;
  closeButtonStyleProps?: React.CSSProperties;
  dialogActionsPDivSxProps?: SxProps;
}

const ActionWall: React.FC<IActionWallProps> = ({
  id,
  open = false,
  title,
  cancelLabel,
  successLabel,
  children,
  onCancel,
  onSuccess,
  onClose,
  onCloseIcon,
  closeIconButton = false,
  sxTitle,
  titleClassName,
  dividers,
  successDisabled,
  cancelButtonId,
  saveButtonId,
  dialogId,
  size,
  outlinedCancelButton,
  headerContent,
  footerContent,
  centerAlignment,
  showCancelBtnAsPrimary,
  dialogContentSxProps,
  dialogActionsSxProps,
  saveBtnSxProps,
  dialogPaperSxProps,
  dialogTitleSxProps,
  closeIconStyleProps,
  closeButtonStyleProps,
  dialogActionsPDivSxProps,
}: IActionWallProps) => {
  const { t: translate } = useTranslation();
  return (
    <Dialog
      id={id}
      data-testid={id}
      open={open}
      onClose={onClose}
      maxWidth={size ? (size as Breakpoint) : 'sm'}
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: { pt: 3, pb: onCancel || onSuccess ? 3 : 0, ...dialogPaperSxProps },
        id: `${dialogId}`,
      }}
    >
      <DialogTitle
        role='presentation'
        sx={{ p: 0, mb: 2, px: 3, ...(dialogTitleSxProps ?? {}) }}
      >
        <Grid container sx={{ position: 'relative' }} alignItems='center'>
          {(title || headerContent) && (
            <Grid item flexGrow={1}>
              <Typography
                sx={{
                  mt: 1,
                  '&:focus': {
                    width: 'fit-content',
                  },
                  ...sxTitle,
                }}
                className={titleClassName ?? 'kfic2-smallHeaderBold'}
                id={`${id}-title`}
                data-testid={`${id}-title`}
                aria-level={1}
                tabIndex={0}
              >
                {title}
              </Typography>
              {headerContent ? headerContent : <></>}
            </Grid>
          )}
          {closeIconButton ? (
            <IconButton
              id={`${id}-close`}
              data-testid={`${id}-close`}
              role='presentation'
              aria-label={translate('modalCloseButtonLabel')}
              onClick={onCloseIcon || onCancel}
              sx={{
                position: 'absolute',
                right: 0,
                ...closeButtonStyleProps,
              }}
            >
              <CloseSVG style={{ ...closeIconStyleProps }} />
            </IconButton>
          ) : (
            <></>
          )}
        </Grid>
      </DialogTitle>

      <DialogContent
        id={`${id}_childEl`}
        dividers={dividers}
        sx={{
          borderTop: 'none',
          mb: 3,
          p: 0,
          pb: onCancel || onSuccess ? 4 : 0,
          ...dialogContentSxProps,
        }}
      >
        {children}
      </DialogContent>
      {(onCancel || onSuccess || footerContent) && (
        <Grid container>
          {footerContent ? footerContent : <></>}
          <Grid
            item
            flexGrow={1}
            justifyContent={'flex-end'}
            sx={{ ...dialogActionsPDivSxProps }}
          >
            <DialogActions
              sx={{
                py: 0,
                px: onCancel || onSuccess ? 3 : 0,
                ...dialogActionsSxProps,
              }}
            >
              {onCancel && (
                <KFButton
                  data-testid={cancelButtonId}
                  id={cancelButtonId}
                  onClick={onCancel}
                  color={showCancelBtnAsPrimary ? 'primary' : 'quaternary'}
                  role='presentation'
                  aria-label={`${cancelLabel} Button Press Enter to Exit the Window`}
                  sx={
                    !outlinedCancelButton
                      ? { border: 'none !important' }
                      : { border: 'inherit' }
                  }
                >
                  {cancelLabel ?? translate('cancel')}
                </KFButton>
              )}
              {onSuccess && (
                <KFButton
                  id={saveButtonId}
                  data-testid={saveButtonId}
                  role='presentation'
                  aria-label={`${successLabel} Button Press Enter to ${successLabel}`}
                  color='primary'
                  onClick={onSuccess}
                  disabled={successDisabled}
                  sx={{ m: centerAlignment ? '0 auto' : '', ...saveBtnSxProps }}
                >
                  {successLabel ?? translate('continue')}
                </KFButton>
              )}
            </DialogActions>
          </Grid>
        </Grid>
      )}
    </Dialog>
  );
};

export default ActionWall;
